"use client";

import { useState, useRef, useEffect } from "react";
import { Mic, StopCircle, Eye, EyeOff, Languages, Speech } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AudioWave from "@/components/ui/audio-wave";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { TextDiffHighlight } from "@/lib/text-diff-highlight-improved";
import { calculateOptimalSimilarity } from "@/lib/text-similarity";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { calculateTextDiff, DiffType } from "@/lib/text-diff-highlight-improved";
import { useUser } from "@/lib/context/user-context";
import ReactMarkdown from "react-markdown";
import { useI18n } from "@/lib/i18n";

export interface ExampleData {
  english: string;
  chinese?: string;
  audioUrl?: string;
}

export function ExampleShadowButton({ example, exampleSequence, courseSlug, idiomId, idiomTitle }: { example: ExampleData; exampleSequence: number; courseSlug: string; idiomId: number; idiomTitle: string }) {
  const [open, setOpen] = useState(false);
  const [showSentence, setShowSentence] = useState(true);
  const [showTranslation, setShowTranslation] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const { user } = useUser();
  const { t } = useI18n();
  const {
    isRecording,
    startRecording,
    stopRecording,
    transcribedText,
    isTranscribing,
    transcriptionError,
    audioUrl,
    audioLevel,
  } = useAudioRecorder();

  // Save result to Redis after we get a transcription
  useEffect(() => {
    const save = async () => {
      if (!transcribedText || !user) return;
      const accuracy = calculateOptimalSimilarity(example.english, transcribedText);
      const durationSec = startTimeRef.current ? Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000)) : 0;
      const segments = calculateTextDiff(example.english, transcribedText);
      const missing = segments
        .filter(s => s.type === DiffType.DELETE)
        .map(s => (s as any).text || (s as any).originalText || "")
        .filter(Boolean);
      // Match GroupedTranscriptEntry format: originalWord → userWord
      const wrong = segments
        .filter(s => s.type === DiffType.SUBSTITUTE)
        .map((s: any) => `${s.originalText ?? ''} → ${s.text ?? ''}`.trim())
        .filter(Boolean);
      const extra = segments
        .filter(s => s.type === DiffType.INSERT)
        .map(s => (s as any).text || "")
        .filter(Boolean);

      // Build detailedMistakes with positions, keeping parity with GroupedTranscriptEntry
      const detailedMissing: Array<{ word: string; originalPosition: number }> = [];
      const detailedWrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }> = [];
      const detailedExtra: Array<{ word: string; userPosition: number }> = [];
      let originalPosition = 0;
      let userPosition = 0;
      segments.forEach((seg: any) => {
        switch (seg.type) {
          case DiffType.DELETE:
            detailedMissing.push({ word: seg.text, originalPosition });
            originalPosition++;
            break;
          case DiffType.INSERT:
            detailedExtra.push({ word: seg.text, userPosition });
            userPosition++;
            break;
          case DiffType.SUBSTITUTE:
            detailedWrong.push({ originalWord: seg.originalText, userWord: seg.text, originalPosition, userPosition });
            originalPosition++;
            userPosition++;
            break;
          case DiffType.EQUAL:
            originalPosition++;
            userPosition++;
            break;
        }
      });

      try {
        const res = await fetch('/api/shadowing-stats', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            record: {
              episodeId: `course:${courseSlug}:${idiomId}`,
              episodeTitle: idiomTitle,
              episodeThumbnail: '/images/cover.png',
              sentenceText: example.english,
              userTranscription: transcribedText,
              accuracy,
              duration: durationSec,
              attemptNumber: attempt + 1,
              exampleSequence,
              mistakes: { missing, wrong, extra, detailedMistakes: { missing: detailedMissing, wrong: detailedWrong, extra: detailedExtra } },
            }
          })
        });
        if (res.ok) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('shadowingRecordSaved', {
              detail: { episodeId: `course:${courseSlug}:${idiomId}`, accuracy }
            }));
          }, 300);
        }
      } catch (e) {
        console.error('Failed to save shadowing record:', e);
      }
    };
    save();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribedText]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 px-2"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={t("shadowing.title")}
        title={t("shadowing.title")}
      >
        <Speech className="h-4 w-4 mr-1" /> {t("quizSection.shadowing.cta")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl border-gray-200">
          <DialogHeader>
            <DialogTitle>{t("shadowing.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">{t("quizSection.shadowing.instructions")}</div>
              <div className="flex items-center gap-2">
                {example.chinese && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => setShowTranslation((v) => !v)}
                  >
                    <Languages className="h-4 w-4 mr-1" />
                    {showTranslation ? t("quizSection.shadowing.hideTranslation") : t("quizSection.shadowing.showTranslation")}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => setShowSentence((v) => !v)}
                >
                  {showSentence ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                  {showSentence ? t("mobileImmersive.shadowing.hideText") : t("mobileImmersive.shadowing.showText")}
                </Button>
              </div>
            </div>

            {showSentence && (
              <div className="mb-2 prose prose-sm dark:prose-invert max-w-none prose-em:font-semibold prose-em:text-primary">
                <ReactMarkdown>{example.english}</ReactMarkdown>
              </div>
              // <p className="text-base leading-relaxed">{example.english}</p>
            )}
            {showTranslation && example.chinese && (
              <p className="text-sm text-muted-foreground whitespace-pre-line">{example.chinese}</p>
            )}

            {example.audioUrl && (
              <AudioWave url={example.audioUrl} dark={false} autoPlay={false} />
            )}

            {/* Dictation-style circular record button, same UI logic as GroupedTranscriptEntry */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={async () => {
                          if (isRecording) {
                            stopRecording();
                            // recording ended, keep attempt count
                          } else {
                            await startRecording();
                            startTimeRef.current = Date.now();
                            setAttempt((a) => a + 1);
                          }
                        }}
                        disabled={isTranscribing}
                        aria-label={isRecording ? t("shadowing.stopRecording") : t("shadowing.startShadowing")}
                        aria-pressed={isRecording}
                        aria-busy={isTranscribing}
                        className={`
                          w-9 h-9 border p-1.5 rounded-full cursor-pointer
                          inline-flex items-center justify-center
                          focus:bg-black/[0.15] dark:focus:bg-white/[0.7]
                          focus:outline-none focus-visible:outline-2 focus-visible:outline-black/80 dark:focus-visible:outline-white/80 focus-visible:outline-offset-2
                          ${isTranscribing ? 'opacity-50 cursor-not-allowed' : ''}
                          ${transcriptionError ? 'bg-red-100 hover:bg-red-200' : ''}
                        `}
                        style={{
                          boxSizing: 'border-box',
                          transition: isRecording ? 'background-color 0.2s ease' : 'box-shadow 0.05s linear, background-color 0.2s ease',
                          boxShadow: isRecording
                            ? `0 0 0 ${Math.max(0, Math.min(4, audioLevel * 6))}px rgba(0, 0, 0, ${Math.max(0.05, Math.min(0.4, audioLevel * 0.4))})`
                            : '0 0 0 0px rgba(0, 0, 0, 0)'
                        }}
                      >
                        {/* Idle state - mic */}
                        {!isRecording && !isTranscribing && !transcriptionError && (
                          <svg
                            className="w-full h-full text-black/80 dark:text-black"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                          </svg>
                        )}
                        {/* Recording - red dot */}
                        {isRecording && (
                          <svg className="w-full h-full text-red-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                        )}
                        {/* Processing spinner */}
                        {isTranscribing && (
                          <svg
                            className="w-full h-full text-black/80 dark:text-black animate-spin"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M12 2v4" />
                            <path d="m16.2 7.8 2.9-2.9" />
                            <path d="M18 12h4" />
                            <path d="m16.2 16.2 2.9 2.9" />
                            <path d="M12 18v4" />
                            <path d="m4.9 19.1 2.9-2.9" />
                            <path d="M2 12h4" />
                            <path d="m4.9 4.9 2.9 2.9" />
                          </svg>
                        )}
                        {/* Error state */}
                        {transcriptionError && !isRecording && !isTranscribing && (
                          <svg className="w-full h-full text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="12" x2="12" y1="4" y2="14" />
                            <line x1="12" x2="12.01" y1="20" y2="20" />
                          </svg>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{isRecording ? t("shadowing.stopRecording") : t("shadowing.startShadowing")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {audioUrl && (
                <div className="w-full space-y-1">
                  {/* <div className="text-xs text-muted-foreground">Your recording</div> */}
                  <AudioWave url={audioUrl} dark={false} autoPlay={false} />
                </div>
              )}
            </div>

            {isTranscribing && (
              <div className="text-sm text-muted-foreground">{t("course.examples.transcribing")}</div>
            )}
            {transcriptionError && (
              <div className="text-sm text-red-600">{transcriptionError}</div>
            )}
            {transcribedText && (
              <TextDiffHighlight
                originalText={example.english}
                userTranscription={transcribedText}
                accuracy={calculateOptimalSimilarity(example.english, transcribedText)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
